import { useState, useEffect } from "react";
import { Star, Quote, ExternalLink } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL
  ? `${process.env.REACT_APP_BACKEND_URL}/api`
  : '/api';

function StarRating({ rating }) {
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <Star
          key={star}
          className={`w-4 h-4 ${
            star <= rating
              ? "fill-amber-400 text-amber-400"
              : "fill-gray-200 text-gray-200"
          }`}
        />
      ))}
    </div>
  );
}

function TestimonialCard({ testimonial }) {
  const initials = testimonial.author_name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

  return (
    <div className="bg-white rounded-2xl p-6 shadow-soft hover:shadow-hover transition-shadow duration-300">
      {/* Header */}
      <div className="flex items-start gap-4 mb-4">
        {/* Avatar */}
        {testimonial.avatar_url ? (
          <img
            src={testimonial.avatar_url}
            alt={testimonial.author_name}
            className="w-12 h-12 rounded-full object-cover"
          />
        ) : (
          <div className="w-12 h-12 bg-forest rounded-full flex items-center justify-center flex-shrink-0">
            <span className="font-body font-semibold text-white text-sm">
              {initials}
            </span>
          </div>
        )}

        <div className="flex-1">
          <h4 className="font-body font-semibold text-forest">
            {testimonial.author_name}
          </h4>
          <div className="flex items-center gap-2 mt-1">
            <StarRating rating={testimonial.rating} />
            <span className="text-xs text-forest/50 font-body">
              {new Date(testimonial.date).toLocaleDateString('fr-FR', {
                month: 'long',
                year: 'numeric'
              })}
            </span>
          </div>
        </div>

        {/* Google icon */}
        {testimonial.source === "google" && (
          <div className="flex-shrink-0">
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
          </div>
        )}
      </div>

      {/* Visit date if available */}
      {testimonial.visit_date && (
        <p className="font-body text-xs text-autumn mb-2">{testimonial.visit_date}</p>
      )}

      {/* Content */}
      <div className="relative">
        <Quote className="absolute -top-2 -left-1 w-6 h-6 text-sand/50" />
        <p className="font-body text-sm text-forest/80 leading-relaxed pl-5 whitespace-pre-line">
          {testimonial.content}
        </p>
      </div>
    </div>
  );
}

export default function TestimonialsSection() {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTestimonials = async () => {
      try {
        const response = await fetch(`${API}/testimonials`);
        if (response.ok) {
          const data = await response.json();
          setTestimonials(data);
        }
      } catch (error) {
        console.error("Error fetching testimonials:", error);
      }
      setLoading(false);
    };
    fetchTestimonials();
  }, []);

  const featuredTestimonials = testimonials.filter(t => t.is_featured);
  const averageRating = testimonials.length > 0 
    ? (testimonials.reduce((acc, t) => acc + t.rating, 0) / testimonials.length).toFixed(1)
    : "5.0";

  return (
    <section
      id="temoignages"
      data-testid="testimonials-section"
      className="relative py-24 lg:py-32 bg-forest overflow-hidden"
    >
      {/* Decorative elements */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-forest-light/20 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-12">
        {/* Section header */}
        <div className="section-animate text-center mb-12">
          <span className="font-accent text-autumn text-xl">Ce qu'ils disent de nous</span>
          <h2 className="font-heading text-4xl sm:text-5xl font-bold text-white mt-2 mb-6">
            Avis de Nos Adoptants
          </h2>
          <p className="font-body text-lg text-cream/80 max-w-2xl mx-auto mb-8">
            Découvrez les témoignages authentiques des familles qui ont adopté
            un chaton chez La Taïga Siberia.
          </p>

          {/* Google rating summary */}
          <div className="inline-flex items-center gap-4 bg-white/10 backdrop-blur-sm rounded-full px-6 py-3">
            <div className="flex items-center gap-2">
              <svg className="w-6 h-6" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              <span className="font-body font-semibold text-white">Google</span>
            </div>
            <div className="h-6 w-px bg-white/20" />
            <div className="flex items-center gap-2">
              <span className="font-heading text-2xl font-bold text-white">
                {averageRating}
              </span>
              <StarRating rating={Math.round(parseFloat(averageRating))} />
            </div>
            <div className="h-6 w-px bg-white/20" />
            <span className="font-body text-sm text-cream/80">
              {testimonials.length} avis
            </span>
          </div>
        </div>

        {/* Testimonials - centered layout for single/few reviews */}
        <div className="section-animate">
          {featuredTestimonials.length === 1 ? (
            <div className="max-w-2xl mx-auto">
              <TestimonialCard testimonial={featuredTestimonials[0]} />
            </div>
          ) : (
            <div className={`grid gap-6 ${
              featuredTestimonials.length === 2 
                ? 'sm:grid-cols-2 max-w-4xl mx-auto' 
                : featuredTestimonials.length === 3 
                  ? 'sm:grid-cols-2 lg:grid-cols-3 max-w-5xl mx-auto'
                  : 'sm:grid-cols-2 lg:grid-cols-4'
            }`}>
              {featuredTestimonials.map((testimonial) => (
                <TestimonialCard key={testimonial.id} testimonial={testimonial} />
              ))}
            </div>
          )}
        </div>

        {/* CTA */}
        <div className="section-animate text-center mt-12">
          <a
            href="https://www.google.com/search?q=La+Taïga+Siberia+avis"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-cream/80 hover:text-white font-body transition-colors"
            data-testid="google-reviews-link"
          >
            <span>Voir tous les avis sur Google</span>
            <ExternalLink className="w-4 h-4" />
          </a>
        </div>
      </div>
    </section>
  );
}
